import React from "react";
import { Link } from "react-router-dom";
import { SEO } from '../components/SEO';
import { SEO_CONTENT } from '../data/seoContent';

const tables = [
  {
    industry: "OLED / Display",
    tone: "text-violet-700 bg-violet-50",
    rows: [
      ["HTL / HIL", "Sublimed", "< 50 ppb", "Hole transport / injection"],
      ["ETL / EIL", "Sublimed", "< 50 ppb", "Electron transport / injection"],
      ["Host materials", "Sublimed", "< 50 ppb", "Emissive-layer host"],
      ["Emitters & dopants", "Sublimed", "< 50 ppb", "Emissive layer (light emission)"],
      ["Deuterated emitters & intermediates", "Electronic", "< 50 ppb", "Efficiency / lifetime"],
      ["Charge-generation layer (CGL)", "Sublimed", "< 50 ppb", "Tandem stacks"],
    ],
  },
  {
    industry: "Semiconductor",
    tone: "text-sky-700 bg-sky-50",
    rows: [
      ["ArF / EUV photoresist monomers", "Electronic", "sub-ppb", "Advanced lithography"],
      
      ["High-purity specialty intermediates", "Custom", "< 50 ppb", "Process chemicals"],
      ["Custom electronic-grade reagents", "Custom", "to spec", "Made-to-order"],
    ],
  },
  {
    industry: "Specialty chemicals",
    tone: "text-emerald-700 bg-emerald-50",
    rows: [
      ["Cosmetic active materials", "Custom", "to spec", "Personal-care actives"],
      
      ["Custom high-purity intermediates", "Custom", "to spec", "Made-to-order"],
    ],
  },
];

const grades = [
  ["Sublimed grade", "Highest-purity grade for OLED / display use."],
  ["Electronic grade", "Metal impurities controlled below 50 ppb."],
  ["Ultra-high purity", "Purified and verified to your specification."],
  ["Custom spec", "Made to your purity, impurity, and documentation profile."],
];

const cols = ["Material family", "Typical grade", "Metal impurities", "Lead application"];

export default function Products() {
  return (
    <div className="min-h-screen bg-white">
      <SEO {...SEO_CONTENT.products} />
      {/* Hero */}
      <section className="relative bg-gradient-to-b from-slate-900 to-slate-800 text-white py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">Products &amp; Capabilities</h1>
          <p className="text-teal-400 text-lg md:text-xl">
            Made-to-order high-purity materials across display, semiconductor, and specialty chemistry.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">What we make</h2>
        <p className="text-slate-600 max-w-3xl">
          LAMKO is a custom synthesis house - most of what we deliver is made to order against your
          specification. The tables below show the chemistry we work in and the grades we routinely deliver.
          If you don&apos;t see your exact material, that&apos;s expected: bring us the target and we&apos;ll design the route.
        </p>
      </section>

      {/* Spec tables by industry */}
      <section className="max-w-6xl mx-auto px-6 pb-8 space-y-10">
        {tables.map((t) => (
          <div key={t.industry}>
            <div className={`inline-block rounded-md px-3 py-1 text-sm font-semibold mb-3 ${t.tone}`}>{t.industry}</div>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-100 text-slate-600">
                    {cols.map((c) => (
                      <th key={c} scope="col" className="px-4 py-3 text-left font-medium">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {t.rows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-200 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">{r[0]}</td>
                      <td className="px-4 py-3 font-mono tabular-nums text-slate-700">{r[1]}</td>
                      <td className="px-4 py-3 font-mono tabular-nums text-teal-700">{r[2]}</td>
                      <td className="px-4 py-3 text-slate-600">{r[3]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        <p className="text-sm text-slate-500">
          Purity and impurity figures are typical capability values, confirmed per lot by Certificate of Analysis.
          Final specifications are agreed per program.
        </p>
      </section>

      {/* Grades */}
      <section className="bg-slate-50 border-y border-slate-200 py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">Purity grades we deliver</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {grades.map(([g, d]) => (
              <div key={g} className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-900 mb-1">{g}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom CTA */}
      <section className="max-w-5xl mx-auto px-6 py-16 text-center">
        <div className="bg-slate-900 text-white rounded-2xl p-10">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Don&apos;t see your material?</h2>
          <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
            Custom synthesis is what we do. Send us a structure or a target spec, and we&apos;ll come back
            - under NDA - with a feasibility view and a route to scale.
          </p>
          <Link to="/get-in-touch" className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg px-8 py-3 transition-colors">
            Connect with us &rarr;
          </Link>
        </div>
      </section>
    </div>
  );
}
